/**
 * Service Integration Schema Validator
 * Validates integration service data against the API schema requirements
 * (requestBody validation intentionally disabled per latest requirement)
 */

import { isFieldSelected } from './manage-integration-service/utils/filename-field-options.js'

export const MAX_THROTTLE_MS = 86400000 // 24 hours

/**
 * Validates an integration service object against the API schema
 * @param {Object} data - The integration service data to validate
 * @param {Object} [options] - Optional validation flags
 * @param {boolean} [options.validateName=true] - When false, skips validating the `name` field
 * @param {boolean} [options.isDirectToS3=false] - When true, allows Step 2 (connection) and Step 4 (response handling) fields to be null
 * @throws {Error} - Throws an error if validation fails
 */
export function validateIntegrationServiceSchema (data, options = {}) {
	const shouldValidateName = options.validateName ?? true
	// Direct to S3 mode: allow connection (step 2) and response handling (step 4) fields to be null
	const isDirectToS3 = options.isDirectToS3 ?? (data?.authenticationTypeId === 5 || data?.systemAuthenticationTypeId === 5)
	// Required field validation
	if (shouldValidateName && !data.name) {
		throw new Error('Service name is required')
	}

	// String length validation for name
	if (shouldValidateName) {
		if (typeof data.name !== 'string' ||
			data.name.length < 1 ||
			data.name.length > 100) {
			throw new Error('Service name must be between 1 and 100 characters')
		}
	}

	// Integer validation with minimum constraints
	if (data.integrationId !== undefined && data.integrationId !== null) {
		if (!Number.isInteger(data.integrationId) || data.integrationId <= 0) {
			throw new Error('integrationId must be a positive integer')
		}
	}

	if (data.brandId !== undefined && data.brandId !== null) {
		if (!Number.isInteger(data.brandId)) {
			throw new Error('brandId must be an integer')
		}
	}

	if (data.cassieObjectId !== undefined && data.cassieObjectId !== null) {
		if (!Number.isInteger(data.cassieObjectId)) {
			throw new Error('cassieObjectId must be an integer')
		}
	}

	if (data.requestThrottlingWaitTimeMS !== undefined && data.requestThrottlingWaitTimeMS !== null) {
		let requestThrottlingWaitTimeMS = data.requestThrottlingWaitTimeMS

		if (typeof requestThrottlingWaitTimeMS === 'string') {
			const trimmed = requestThrottlingWaitTimeMS.trim()
			requestThrottlingWaitTimeMS = trimmed === ''
				? NaN
				: Number(trimmed)
		}

		if (typeof requestThrottlingWaitTimeMS !== 'number' || !Number.isInteger(requestThrottlingWaitTimeMS) || requestThrottlingWaitTimeMS < 0 || requestThrottlingWaitTimeMS > MAX_THROTTLE_MS) {
			throw new Error(`requestThrottlingWaitTimeMS must be a non-negative integer no greater than ${MAX_THROTTLE_MS} (24 hours)`)
		}
	}

	// Custom S3 file name: the chosen field must be one of those selected for export,
	// otherwise it is never extracted and the name silently falls back to the default.
	// Gated on Direct to S3 like the other step-specific rules: the setting is meaningless
	// for any other authentication type, and there is no UI to clear a stale value, so
	// enforcing it there would dead-end an otherwise valid save or import.
	if (isDirectToS3 && data.useCustomFileName) {
		if (!data.customFileNameField) {
			throw new Error('A filename field is required when using a customised file name format')
		}

		const selectList = Array.isArray(data.objectSelectList)
			? data.objectSelectList
			: String(data.objectSelectList || '').split(',')
		// Entries are plain names once loaded, but field objects while step 1 is being edited.
		const isSelected = isFieldSelected(selectList, data.customFileNameField)

		if (!isSelected) {
			throw new Error('The filename field must be one of the fields selected in step 1')
		}
	}

	// Boolean validation
	if (data.isEnabled !== undefined && data.isEnabled !== null) {
		if (typeof data.isEnabled !== 'boolean') {
			throw new Error('isEnabled must be a boolean value')
		}
	}

	if (data.updateStandDak !== undefined && data.updateStandDak !== null) {
		if (typeof data.updateStandDak !== 'boolean') {
			throw new Error('updateStandDak must be a boolean value')
		}
	}

	if (data.resetIncrementalValue !== undefined && data.resetIncrementalValue !== null) {
		if (typeof data.resetIncrementalValue !== 'boolean') {
			throw new Error('resetIncrementalValue must be a boolean value')
		}
	}

	// Array validation
	// Step 2 (connection): customHeaders may be null when Direct to S3
	if (!isDirectToS3 && data.customHeaders !== undefined && data.customHeaders !== null) {
		if (!Array.isArray(data.customHeaders)) {
			throw new Error('customHeaders must be an array')
		}
	}

	// Step 4 (response handling): successResponses may be null when Direct to S3
	if (!isDirectToS3 && data.successResponses !== undefined && data.successResponses !== null) {
		if (!Array.isArray(data.successResponses)) {
			throw new Error('successResponses must be an array')
		}
	}

	if (data.filters !== undefined && data.filters !== null) {
		if (!Array.isArray(data.filters)) {
			throw new Error('filters must be an array')
		}
	}

	// URL validation (basic check) — allow full URLs or partial paths
	// Step 2 (connection): requestUrl may be null when Direct to S3
	if (!isDirectToS3 && data.requestUrl && typeof data.requestUrl === 'string') {
		const s = data.requestUrl.trim()
		// Accept full http(s) URLs, or partial paths like /api/..., ./..., ../..., or scheme-less path segments with no spaces
		const isFullHttp = /^https?:\/\/.+/i.test(s)
		const isPartialPath = /^(\/|\.\.?\/)/.test(s) || (!/\s/.test(s) && !/^[a-z]+:\/\//i.test(s))
		if (!isFullHttp && !isPartialPath) {
			throw new Error('requestUrl must be an HTTP/HTTPS URL or a path (e.g., /api/endpoint)')
		}
	}

	// Step 2 (connection): authenticationUrl may be null when Direct to S3
	if (!isDirectToS3 && data.authenticationUrl && typeof data.authenticationUrl === 'string') {
		try {
			if (!data.authenticationUrl.match(/^https?:\/\/.+/)) {
				throw new Error('authenticationUrl must be a valid HTTP/HTTPS URL')
			}
		} catch (error) {
			throw new Error('authenticationUrl must be a valid URL')
		}
	}

	// HTTP method validation
	// Step 2 (connection): httpMethod may be null when Direct to S3
	if (!isDirectToS3 && data.httpMethod && typeof data.httpMethod === 'string') {
		const validMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
		if (!validMethods.includes(data.httpMethod.toUpperCase())) {
			throw new Error('httpMethod must be one of: ' + validMethods.join(', '))
		}
	}

	// requestBody validation intentionally skipped
	// (Previously: presence + JSON parse enforcement)
}
// Checks each filter for missing values. Returns an array of error strings (empty = valid).
// Needed because VeeValidate providers inside v-data-table slots don't register
// with the outer ValidationObserver in Vue 2.
export function validateIntegrationFilters (filters) {
	if (!Array.isArray(filters) || filters.length === 0) return []

	// Operators that don't require a value
	const NO_VALUE_OPERATORS = ['IS NULL', 'IS NOT NULL', 'IS', 'IS NOT']
	const BETWEEN_OPERATORS = ['BETWEEN', 'NOT BETWEEN']
	const MULTI_VALUE_OPERATORS = ['INCLUDES', 'DOES NOT INCLUDE', 'IN', 'NOT IN']
	const errors = []

	for (const filter of filters) {
		if (!filter.filter || !filter.filter.operator) continue

		const operator = filter.filter.operator.toUpperCase()
		if (NO_VALUE_OPERATORS.includes(operator)) continue

		const values = Array.isArray(filter.filter.values) ? filter.filter.values : []
		const fieldLabel = filter.fieldName

		if (BETWEEN_OPERATORS.includes(operator)) {
			// BETWEEN requires both min and max
			const val0 = values[0]?.value
			const val1 = values[1]?.value
			const missing0 = val0 == null || val0 === ''
			const missing1 = val1 == null || val1 === ''
			if (missing0 && missing1) {
				errors.push(`${fieldLabel} - Both values required for ${operator}`)
			} else if (missing0) {
				errors.push(`${fieldLabel} - Minimum value required for ${operator}`)
			} else if (missing1) {
				errors.push(`${fieldLabel} - Maximum value required for ${operator}`)
			}
		} else if (MULTI_VALUE_OPERATORS.includes(operator)) {
			// INCLUDES / IN etc. need at least one value
			const hasValue = values.some(v => v.value != null && v.value !== '')
			if (!hasValue) {
				errors.push(`${fieldLabel} - At least one value required for ${operator}`)
			}
		} else {
			// All other operators (EQUALS, GREATER THAN, etc.) need a single value
			const val = values[0]?.value
			if (val == null || val === '') {
				errors.push(`${fieldLabel} - Value required for ${operator}`)
			}
		}
	}

	return errors
}

/**
 * Validates if a string is a valid ISO 8601 datetime
 * @param {string} dateString - The datetime string to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidDateTime (dateString) {
	if (!dateString || typeof dateString !== 'string') {
		return false
	}

	// Check ISO 8601 format
	const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[+-]\d{2}:\d{2})$/

	if (!iso8601Regex.test(dateString)) {
		return false
	}

	// Check if the date is actually valid
	const date = new Date(dateString)
	return date instanceof Date && !isNaN(date.getTime())
}

/**
 * Validates import data for integration service
 * @param {string} jsonData - The JSON string to validate
 * @returns {Object} - Object with isValid boolean and error string
 */
export function validateServiceImportData (jsonData) {
	try {
		// Check if data is provided
		if (!jsonData || typeof jsonData !== 'string') {
			return {
				isValid: false,
				error: 'Please provide service configuration data'
			}
		}

		// Trim whitespace and handle common formatting issues
		jsonData = jsonData.trim()

		// Fix common pasting issues - replace literal \n with actual newlines
		// This handles cases where JSON is copied with escaped newlines
		if (jsonData.includes('\\n') && !jsonData.includes('\n')) {
			jsonData = jsonData.replace(/\\n/g, '\n')
		}

		// Fix escaped quotes that might get doubled during copy/paste
		jsonData = jsonData.replace(/\\\\"/g, '\\"')

		// Fix any double-escaped backslashes
		jsonData = jsonData.replace(/\\\\\\\\/g, '\\\\')

		if (jsonData === '') {
			return {
				isValid: false,
				error: 'Please provide service configuration data'
			}
		}

		// Try to parse JSON
		let parsedData
		try {
			parsedData = JSON.parse(jsonData)
		} catch (jsonError) {
			return {
				isValid: false,
				error: 'Invalid JSON format: ' + jsonError.message
			}
		}

		// Check if it's an object
		if (typeof parsedData !== 'object' || parsedData === null || Array.isArray(parsedData)) {
			return {
				isValid: false,
				error: 'Service configuration must be a JSON object'
			}
		}

		// Validate against schema
		try {
			validateIntegrationServiceSchema(parsedData)
		} catch (schemaError) {
			return {
				isValid: false,
				error: 'Invalid service configuration: ' + schemaError.message
			}
		}

		return {
			isValid: true,
			error: null,
			data: parsedData,
			cleanedJsonString: jsonData
		}
	} catch (error) {
		return {
			isValid: false,
			error: 'Validation error: ' + error.message
		}
	}
}
